import { ErrorMessage, FieldTextV6, FormRow } from '@tallink/components-lib'
import { mediaQueries } from '@tallink/components-lib/src/constants/media'
import * as PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useMedia } from 'react-use'

import {
  agentLoggedIn,
  companyLoggedIn,
  updateAgentIdFieldValue,
  updateCompanyIdFieldValue
} from '../../../actions/actions'
import SerialTicketsClient from '../../../clients/SerialTicketsClient'
import { getAgent, getCompany } from '../../../selectors/selectors'
import Button from '../../common/Button'
import SubmitButton from '../../Summary/CustomerLogin/CompanyCustomer/CompanyCustomerLoginForm/SubmitButton/SubmitButton'
import s from './CompanyCustomerLogin.module.scss'
import CompanyInfo from './CompanyInfo'

export default function CompanyCustomerLogin({ onCancelButtonClick, onLoginSuccess }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const smUp = useMedia(mediaQueries.smUp)
  const history = useHistory()
  const company = useSelector(getCompany)
  const { agentIdFieldValue } = useSelector(getAgent)
  const { agentRequired, companyId, companyIdFieldValue } = company
  const {
    errors,
    formState: { isSubmitting },
    handleSubmit,
    register,
    reset,
    setError,
    watch
  } = useForm({
    defaultValues: { agentId: agentIdFieldValue, companyId: companyIdFieldValue },
    mode: 'onChange'
  })
  const companyInputValue = watch('companyId')
  const agentInputValue = watch('agentId')
  const submitButtonActive =
    (companyInputValue || (companyId && agentInputValue)) && !errors.companyId && !errors.agentId && !isSubmitting

  useEffect(() => {
    if (smUp) history.push('/')
  }, [history, smUp])

  return (
    <form className={s['company-customer-login-form']} onSubmit={handleSubmit(login)}>
      <div className={s.header}>{t('companyCustomer.title')}</div>
      {!companyId && (
        <FormRow
          error={<ErrorMessage message={errors.companyId?.message} />}
          invalid={errors.companyId?.type === 'manual'}
          label={t('companyCustomer.id')}
        >
          <FieldTextV6
            disabled={isSubmitting}
            inputRef={register({ pattern: { value: /^\d{1,6}$/ } })}
            invalid={errors.companyId?.type === 'pattern'}
            name="companyId"
            onChange={e => dispatch(updateCompanyIdFieldValue(e.target.value))}
            onClearClick={() => reset('companyId')}
          />
        </FormRow>
      )}
      {companyId && <CompanyInfo company={company} />}
      {companyId && agentRequired && (
        <FormRow
          error={<ErrorMessage message={errors.agentId?.message} />}
          invalid={errors.agentId?.type === 'manual'}
          label={t('companyCustomer.agentId')}
        >
          <FieldTextV6
            disabled={isSubmitting}
            inputRef={register({ pattern: { value: /^\d+$/ } })}
            invalid={errors.agentId?.type === 'pattern'}
            name="agentId"
            onChange={e => dispatch(updateAgentIdFieldValue(e.target.value))}
            onClearClick={() => reset('agentId')}
          />
        </FormRow>
      )}
      <SubmitButton className={s['button-submit']} disabled={!submitButtonActive}>
        {t('mobile.done')}
      </SubmitButton>
      <Button onClick={onCancelButtonClick} type="button">
        {t('common.cancel')}
      </Button>
    </form>
  )

  async function login(formData) {
    if (companyId) await loginWithAgentId(formData)
    else await loginWithCompanyId(formData)
  }

  async function loginWithCompanyId({ companyId }) {
    const company = await SerialTicketsClient.getCompany(companyId)
    company.companyId = companyId

    if (!company || !company.valid) {
      setError('companyId', { type: 'manual', message: t('companyCustomer.error') })
    } else {
      dispatch(companyLoggedIn(company))
      if (!company.agentRequired) {
        onLoginSuccess()
      }
    }
  }

  async function loginWithAgentId({ agentId }) {
    const agent = await SerialTicketsClient.getAgent(agentId, companyId)

    if (!agent?.valid) {
      setError('agentId', { type: 'manual', message: t('companyCustomer.agentId.error') })
    } else {
      dispatch(agentLoggedIn({ ...agent, agentId }))
      onLoginSuccess()
    }
  }
}

CompanyCustomerLogin.propTypes = {
  onCancelButtonClick: PropTypes.func.isRequired,
  onLoginSuccess: PropTypes.func.isRequired
}
